import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, BookOpen, Mail, MessageSquareText, PhoneCall, PlayCircle } from 'lucide-react';
import { supportAPI } from '../../../api/client';
import { apiError } from '../../../lib/host';
import { useT } from '../../../lib/i18n';
import { ErrorBanner, Modal, PageHeading, useBasic } from '../ui';
import HelpArticles from './components/HelpArticles';
import MyTickets from './components/MyTickets';
import TicketDialog from './components/TicketDialog';
import TicketForm from './components/TicketForm';
import './support.css';

const SUPPORT_EMAIL = 'support@telecalx.com';

function ContactSupport({ onClose, onTicket }) {
  const { t } = useT();
  return (
    <Modal title={t('Contact Support')} onClose={onClose}>
      <div className="hs-contact">
        <p className="st-muted">{t('The quickest way to get help is a support ticket: it reaches the whole support team and you can follow it here.')}</p>
        <a className="hs-contact-row" href={`mailto:${SUPPORT_EMAIL}`}><Mail size={18} /><span><b>{t('Email')}</b><small>{SUPPORT_EMAIL}</small></span></a>
        <button type="button" className="hs-contact-row" onClick={onTicket}><MessageSquareText size={18} /><span><b>{t('Open Support Ticket')}</b><small>{t('Get help from our team')}</small></span><ArrowRight size={14} /></button>
      </div>
    </Modal>
  );
}

/** Help & Support: help articles and guides, opening a ticket, and following your tickets with TeleCalX Support. */
export default function HelpSupportPage() {
  const { t } = useT();
  const { notify } = useBasic();
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState(null); // {category, article}
  const [tickets, setTickets] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [openId, setOpenId] = useState(() => Number(params.get('ticket')) || null);
  const [contact, setContact] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef(null);
  const articlesRef = useRef(null);

  const load = useCallback(() => supportAPI.list({ limit: showAll ? 100 : 5 }).then((r) => setTickets(r.data)).catch((err) => setError(apiError(err))), [showAll]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get('new')) {
      formRef.current?.scrollIntoView({ block: 'start' });
      formRef.current?.querySelector('input')?.focus();
    }
    if (params.get('new') || params.get('ticket')) setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toForm = () => {
    setContact(false);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => formRef.current?.querySelector('input')?.focus(), 300);
  };
  const toArticles = (view) => {
    setArticles(view);
    articlesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const quick = [
    [BookOpen, 'green', t('Help Articles'), t('Browse guides and FAQs'), () => toArticles({ category: 'all' })],
    [PlayCircle, 'blue', t('Video Tutorials'), t('Watch step by step guides'), () => { toArticles({ category: 'guides' }); notify(t('Video tutorials are on the way. Until then, follow these step-by-step guides.')); }],
    [MessageSquareText, 'purple', t('Open Support Ticket'), t('Get help from our team'), toForm],
    [PhoneCall, 'orange', t('Contact Support'), t('Talk to our support team'), () => setContact(true)],
  ];

  return (
    <section className="page-view hs-page">
      <PageHeading title={t('Help & Support')} subtitle={t('Find answers, get help, and manage your support requests.')} root={t('Workspace')} />
      <ErrorBanner message={error} />
      <div className="hs-quick">
        {quick.map(([Icon, tone, title, sub, run]) => (
          <button type="button" key={title} className={`hs-quick-card ${tone}`} onClick={run}>
            <span className={`hs-icon solid ${tone}`}><Icon size={22} /></span>
            <span><b>{title}</b><small>{sub}</small></span><ArrowRight size={18} />
          </button>
        ))}
      </div>
      <div className="hs-grid">
        <div ref={articlesRef}><HelpArticles view={articles} onView={setArticles} /></div>
        <div className="hs-side">
          <TicketForm formRef={formRef} onCreated={(ticket) => {
            notify(t('Ticket {number} sent. We will reply here and by email.', { number: ticket.number }));
            load();
            setOpenId(ticket.id);
          }} />
          <MyTickets data={tickets} showAll={showAll} onView={setOpenId} onViewAll={() => setShowAll(true)} />
        </div>
      </div>
      {openId && <TicketDialog id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      {contact && <ContactSupport onClose={() => setContact(false)} onTicket={toForm} />}
    </section>
  );
}
